/**
 * The Lazada token pair, and the arithmetic that decides what to do with it.
 *
 * Pure on purpose: no SharePoint, no fetch, no clock of its own. Every
 * decision here is "given this pair and this instant, what now?", which is a
 * question with a right answer and therefore a testable one.
 *
 * ── WHY THIS EXISTS AT ALL ────────────────────────────────────────────────
 * Shopify's token is permanent and TikTok's lasts a year, so both live in an
 * app setting and are forgotten. Lazada's do not: the ACCESS token is 7 days,
 * the REFRESH token about 30, and refreshing ROTATES the refresh token — the
 * old one stops working the moment the new one is issued.
 *
 * Two consequences, and they are the whole design:
 *
 *   1. A hand-pasted app setting is wrong by construction. The value it holds
 *      is dead within the month and cannot be updated by the thing that knows
 *      the new one. So the pair lives in a list that can be rewritten.
 *
 *   2. Rotation means the stored pair is the ONLY copy. If a refresh succeeds
 *      and the write of its answer fails, the token we hold is already dead
 *      and the live one is lost — re-authorisation by hand is the only way
 *      back. So the write happens before the new pair is used, and a refresh
 *      is never started unless there is somewhere to put the answer.
 *
 * ── THE DEADLINE THAT MATTERS IS THE REFRESH TOKEN'S ──────────────────────
 * An expired ACCESS token is a non-event: the refresh token mints another one
 * and nobody notices. An expired REFRESH token is a person walking to the
 * Lazada console and authorising again. So `health` reports the access side as
 * a detail and the refresh side as the deadline, and the screen says how many
 * days are left rather than a green tick that means nothing until it doesn't.
 */

/** On the CRM site, beside B2B_Orders and IGCOM_Orders. */
export const LAZADA_TOKENS_LIST = "LAZADA_Tokens";

export interface ILazadaTokens {
  accessToken: string;
  refreshToken: string;
  /** ISO. When the access token stops working. */
  accessExpiresAt: string;
  /** ISO. When the refresh token stops working — the real deadline. */
  refreshExpiresAt: string;
  /** The Lazada seller account the pair belongs to, for the screen to name. */
  account: string;
  country: string;
  updatedAt: string;
  updatedBy: string;
}

export type LazadaTokenState =
  /** No list, or a list with nothing in it — never authorised. */
  | "missing"
  /** Access token good. Nothing to do. */
  | "ok"
  /** Access token gone or nearly gone; the refresh token can fix it silently. */
  | "refresh"
  /** Refresh token gone. Only a person at the Lazada console can fix this. */
  | "expired";

export interface ILazadaTokenHealth {
  state: LazadaTokenState;
  /** Whole days until the REFRESH token dies. Negative once it has. */
  refreshDaysLeft: number;
  /** Whole days until the ACCESS token dies. Negative once it has. */
  accessDaysLeft: number;
  /** True when a refresh should be attempted before using the pair. */
  needsRefresh: boolean;
  /** True when no amount of refreshing will help. */
  needsReauthorise: boolean;
}

/**
 * Refresh when the access token has less than this left, not when it has
 * already expired.
 *
 * A token that dies mid-request fails looking like a permissions problem —
 * Lazada answers with a generic refusal, not "your token just expired" — and
 * that is an afternoon of debugging the signature for something that was
 * never wrong. A day of margin costs one extra refresh per week.
 */
const ACCESS_MARGIN_MS = 24 * 60 * 60 * 1000;

const DAY = 86_400_000;

/** Floor, not round: "0 days left" should mean today, not "sometime today or
 *  yesterday". A deadline that rounds up reads as safer than it is. */
const daysUntil = (iso: string, now: number): number => {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return Number.NEGATIVE_INFINITY;
  return Math.floor((t - now) / DAY);
};

/**
 * What to do with this pair, right now.
 *
 * The order of the checks is the rule: the refresh token is examined FIRST,
 * because a dead refresh token makes the access token's state irrelevant —
 * reporting "needs refresh" for a pair that cannot be refreshed sends the
 * screen into a retry it can never win, and hides the one message that would
 * actually help ("authorise again").
 */
export function tokenHealth(
  tokens: ILazadaTokens | null,
  now: number = Date.now(),
): ILazadaTokenHealth {
  if (!tokens || !tokens.refreshToken) {
    return {
      state: "missing",
      refreshDaysLeft: 0,
      accessDaysLeft: 0,
      needsRefresh: false,
      needsReauthorise: true,
    };
  }

  const refreshDaysLeft = daysUntil(tokens.refreshExpiresAt, now);
  const accessDaysLeft = daysUntil(tokens.accessExpiresAt, now);
  const refreshDead = !Number.isFinite(Date.parse(tokens.refreshExpiresAt))
    || Date.parse(tokens.refreshExpiresAt) <= now;

  if (refreshDead) {
    return {
      state: "expired",
      refreshDaysLeft,
      accessDaysLeft,
      needsRefresh: false,
      needsReauthorise: true,
    };
  }

  const accessT = Date.parse(tokens.accessExpiresAt);
  // An unparseable access expiry is treated as expired rather than as valid.
  // Guessing "probably fine" on a credential is how a silent failure starts.
  const accessStale = !Number.isFinite(accessT) || accessT - now <= ACCESS_MARGIN_MS;

  return {
    state: accessStale ? "refresh" : "ok",
    refreshDaysLeft,
    accessDaysLeft,
    needsRefresh: accessStale,
    needsReauthorise: false,
  };
}

/**
 * Turn the installer's / refresh endpoint's answer into a storable pair.
 *
 * The API gives DURATIONS in seconds; the list keeps ABSOLUTE instants,
 * because a duration is only meaningful beside the moment it was measured and
 * that moment is exactly what a stored row loses.
 */
export function toStoredTokens(
  answer: {
    accessToken: string;
    refreshToken: string;
    accessExpiresAt: number;
    refreshExpiresAt: number;
    account?: string;
    country?: string;
  },
  updatedBy: string,
  now: number = Date.now(),
): ILazadaTokens {
  return {
    accessToken: answer.accessToken,
    refreshToken: answer.refreshToken,
    accessExpiresAt: new Date(answer.accessExpiresAt).toISOString(),
    refreshExpiresAt: new Date(answer.refreshExpiresAt).toISOString(),
    account: answer.account || "",
    country: answer.country || "",
    updatedAt: new Date(now).toISOString(),
    updatedBy,
  };
}

/**
 * How to say the state out loud.
 *
 * Kept beside the arithmetic so the words and the numbers cannot drift apart,
 * and phrased as what happens next rather than as a status — "expired" tells a
 * clerk nothing they can act on.
 */
export function describeHealth(h: ILazadaTokenHealth): string {
  switch (h.state) {
    case "missing":
      return "Not connected. Authorise i-Synergy on Lazada to get a token pair.";
    case "expired":
      return "The connection has lapsed. Nobody opened this screen within the refresh token's "
        + "30 days, so it must be authorised again on Lazada.";
    case "refresh":
      return `Renewing the access token. The connection is good for ${h.refreshDaysLeft} more `
        + `day${h.refreshDaysLeft === 1 ? "" : "s"} without anybody doing anything.`;
    case "ok":
      return `Connected. Renews itself; lapses in ${h.refreshDaysLeft} `
        + `day${h.refreshDaysLeft === 1 ? "" : "s"} if this screen is never opened.`;
  }
}
